/**
 * OperationalTransformEngine - Conflict-free collaborative editing
 * 
 * Implements Operational Transform algorithms for real-time collaboration
 * between multiple attorneys editing the same legal document.
 */

import { 
  Operation, 
  InsertOperation, 
  DeleteOperation, 
  RemoteOperation,
  ConflictResolution,
  AttorneyInfo 
} from '../types/DocumentTypes';

interface TransformResult {
  transformedOperation: Operation;
  transformedAgainst: Operation[];
  conflicts: Conflict[];
}

interface Conflict {
  id: string;
  localOperation: Operation;
  remoteOperation: RemoteOperation;
  type: 'position' | 'content' | 'legal_precedence';
  severity: 'low' | 'medium' | 'high';
  autoResolvable: boolean;
}

export class OperationalTransformEngine {
  private legalPrecedenceRules: LegalPrecedenceRules;

  constructor() {
    this.legalPrecedenceRules = new LegalPrecedenceRules();
  }

  // ============================================================================
  // CORE OPERATIONAL TRANSFORM
  // ============================================================================

  /**
   * Transform local operation against remote operations
   */
  transformOperation(
    localOp: Operation,
    remoteOps: RemoteOperation[]
  ): TransformResult {
    console.log(`[OTEngine] Transforming ${localOp.type} against ${remoteOps.length} remote operations`);
    
    let transformedOp = { ...localOp };
    const transformedAgainst: Operation[] = [];
    const conflicts: Conflict[] = [];
    
    // Transform against each remote operation in sequence
    for (const remoteOp of remoteOps) {
      const result = this.transformAgainstSingle(transformedOp, remoteOp);
      
      if (result.conflict) {
        conflicts.push(result.conflict);
        
        // Apply legal precedence for automatic resolution
        if (result.conflict.autoResolvable) {
          transformedOp = this.applyLegalPrecedence(transformedOp, remoteOp);
        }
      } else {
        transformedOp = result.transformed;
      }
      
      transformedAgainst.push(remoteOp as any);
    }
    
    return {
      transformedOperation: transformedOp,
      transformedAgainst,
      conflicts
    };
  }

  /**
   * Transform operation against single remote operation
   */
  private transformAgainstSingle(
    localOp: Operation,
    remoteOp: RemoteOperation
  ): { transformed: Operation; conflict?: Conflict } {
    // Handle different operation type combinations
    if (localOp.type === 'insert' && remoteOp.type === 'insert') {
      return this.transformInsertInsert(localOp as InsertOperation, remoteOp as InsertOperation);
    }
    
    if (localOp.type === 'insert' && remoteOp.type === 'delete') {
      return this.transformInsertDelete(localOp as InsertOperation, remoteOp as DeleteOperation);
    }
    
    if (localOp.type === 'delete' && remoteOp.type === 'insert') {
      return this.transformDeleteInsert(localOp as DeleteOperation, remoteOp as InsertOperation);
    }
    
    if (localOp.type === 'delete' && remoteOp.type === 'delete') {
      return this.transformDeleteDelete(localOp as DeleteOperation, remoteOp as DeleteOperation);
    }
    
    // Unknown combination
    return { transformed: localOp };
  }

  // ============================================================================
  // TRANSFORM ALGORITHMS
  // ============================================================================

  /**
   * Transform Insert vs Insert operations
   */
  private transformInsertInsert(
    localOp: InsertOperation,
    remoteOp: InsertOperation
  ): { transformed: Operation; conflict?: Conflict } {
    const transformed = { ...localOp };
    
    if (remoteOp.position <= localOp.position) {
      // Remote insert happened before local insert
      transformed.position += remoteOp.content.length;
    } else if (remoteOp.position === localOp.position) {
      // Concurrent insert at same position - potential conflict
      const conflict = this.createPositionConflict(localOp, remoteOp as any);
      
      // Apply tie-breaking rules
      const precedence = this.legalPrecedenceRules.comparePrecedence(
        localOp.attorney,
        remoteOp.attorney
      );
      
      if (precedence === 'local') {
        // Local operation wins, remote is shifted
        // No position adjustment needed
      } else {
        // Remote operation wins, local is shifted
        transformed.position += remoteOp.content.length;
      }
      
      return { transformed, conflict };
    }
    
    return { transformed };
  }

  /**
   * Transform Insert vs Delete operations
   */
  private transformInsertDelete(
    localOp: InsertOperation,
    remoteOp: DeleteOperation
  ): { transformed: Operation; conflict?: Conflict } {
    const transformed = { ...localOp };
    
    if (remoteOp.end <= localOp.position) {
      // Remote delete happened before local insert
      const deletedLength = remoteOp.end - remoteOp.start;
      transformed.position -= deletedLength;
    } else if (remoteOp.start <= localOp.position && localOp.position <= remoteOp.end) {
      // Local insert position was deleted - conflict
      const conflict = this.createContentConflict(localOp, remoteOp as any);
      
      // Resolve by moving insert to start of deleted range
      transformed.position = remoteOp.start;
      
      return { transformed, conflict };
    }
    
    return { transformed };
  }

  /**
   * Transform Delete vs Insert operations
   */
  private transformDeleteInsert(
    localOp: DeleteOperation,
    remoteOp: InsertOperation
  ): { transformed: Operation; conflict?: Conflict } {
    const transformed = { ...localOp };
    
    if (remoteOp.position <= localOp.start) {
      // Remote insert happened before local delete range
      const insertLength = remoteOp.content.length;
      transformed.start += insertLength;
      transformed.end += insertLength;
    } else if (remoteOp.position > localOp.start && remoteOp.position < localOp.end) {
      // Remote insert happened within local delete range
      const insertLength = remoteOp.content.length;
      transformed.end += insertLength;
      
      // This might be a conflict if it changes the meaning
      const conflict = this.createContentConflict(localOp, remoteOp as any);
      return { transformed, conflict };
    }
    
    return { transformed };
  }

  /**
   * Transform Delete vs Delete operations
   */
  private transformDeleteDelete(
    localOp: DeleteOperation,
    remoteOp: DeleteOperation
  ): { transformed: Operation; conflict?: Conflict } {
    const transformed = { ...localOp };
    
    // Check for overlapping deletions
    if (this.rangesOverlap(
      { start: localOp.start, end: localOp.end },
      { start: remoteOp.start, end: remoteOp.end }
    )) {
      // Overlapping deletions - resolve conflict
      const conflict = this.createContentConflict(localOp, remoteOp as any);
      
      // Merge deletion ranges
      const mergedStart = Math.min(localOp.start, remoteOp.start);
      const mergedEnd = Math.max(localOp.end, remoteOp.end);
      
      transformed.start = mergedStart;
      transformed.end = mergedEnd - (remoteOp.end - remoteOp.start); // Adjust for remote deletion
      
      return { transformed, conflict };
    }
    
    if (remoteOp.end <= localOp.start) {
      // Remote delete happened before local delete
      const deletedLength = remoteOp.end - remoteOp.start;
      transformed.start -= deletedLength;
      transformed.end -= deletedLength;
    }
    
    return { transformed };
  }

  // ============================================================================
  // CONFLICT DETECTION & RESOLUTION
  // ============================================================================

  private createPositionConflict(localOp: Operation, remoteOp: RemoteOperation): Conflict {
    return {
      id: `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
      localOperation: localOp,
      remoteOperation: remoteOp,
      type: 'position',
      severity: 'low',
      autoResolvable: true
    };
  }

  private createContentConflict(localOp: Operation, remoteOp: RemoteOperation): Conflict {
    return {
      id: `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
      localOperation: localOp,
      remoteOperation: remoteOp,
      type: 'content',
      severity: 'medium',
      autoResolvable: this.legalPrecedenceRules.canAutoResolve(localOp.attorney, remoteOp.attorney)
    };
  }

  private applyLegalPrecedence(localOp: Operation, remoteOp: RemoteOperation): Operation {
    const precedence = this.legalPrecedenceRules.comparePrecedence(
      localOp.attorney,
      remoteOp.attorney
    );
    
    if (precedence === 'remote') {
      // Remote operation takes precedence, adjust local operation
      return this.adjustOperationForPrecedence(localOp, remoteOp);
    }
    
    // Local operation maintains precedence
    return localOp;
  }

  private adjustOperationForPrecedence(localOp: Operation, remoteOp: RemoteOperation): Operation {
    // Simplified adjustment - will be enhanced with sophisticated logic
    const adjusted = { ...localOp };
    
    if (localOp.type === 'insert' && remoteOp.type === 'insert') {
      // Shift local insert after remote insert
      adjusted.position += remoteOp.content?.length || 0;
    }
    
    return adjusted;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private rangesOverlap(range1: { start: number; end: number }, range2: { start: number; end: number }): boolean {
    return !(range1.end <= range2.start || range2.end <= range1.start);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Transform multiple operations as a batch
   */
  transformOperationBatch(
    localOps: Operation[],
    remoteOps: RemoteOperation[]
  ): TransformResult[] {
    const results: TransformResult[] = [];
    
    for (const localOp of localOps) {
      const result = this.transformOperation(localOp, remoteOps);
      results.push(result);
    }
    
    return results;
  }

  /**
   * Check if operations can be automatically resolved
   */
  canAutoResolveConflicts(conflicts: Conflict[]): boolean {
    return conflicts.every(conflict => conflict.autoResolvable);
  }

  /**
   * Get transform statistics for monitoring
   */
  getTransformStatistics(): {
    totalTransforms: number;
    conflictsDetected: number;
    autoResolved: number;
    manualResolutionRequired: number;
  } {
    // Will be implemented with actual statistics tracking
    return {
      totalTransforms: 0,
      conflictsDetected: 0,
      autoResolved: 0,
      manualResolutionRequired: 0
    };
  }
}

// ============================================================================
// LEGAL PRECEDENCE RULES
// ============================================================================

class LegalPrecedenceRules {
  /**
   * Compare precedence between two attorneys
   */
  comparePrecedence(attorney1: AttorneyInfo, attorney2: AttorneyInfo): 'local' | 'remote' | 'equal' {
    // Rule 1: Partner > Associate > Paralegal > Clerk
    const role1Priority = this.getRolePriority(attorney1.role);
    const role2Priority = this.getRolePriority(attorney2.role);
    
    if (role1Priority !== role2Priority) {
      return role1Priority > role2Priority ? 'local' : 'remote';
    }
    
    // Rule 2: Higher seniority wins for same role
    if (attorney1.seniority !== attorney2.seniority) {
      return attorney1.seniority > attorney2.seniority ? 'local' : 'remote';
    }
    
    // Rule 3: Alphabetical by name for equal attorneys (deterministic)
    const nameComparison = attorney1.name.localeCompare(attorney2.name);
    if (nameComparison !== 0) {
      return nameComparison < 0 ? 'local' : 'remote';
    }
    
    return 'equal';
  }

  /**
   * Check if conflict can be auto-resolved
   */
  canAutoResolve(attorney1: AttorneyInfo, attorney2: AttorneyInfo): boolean {
    const precedence = this.comparePrecedence(attorney1, attorney2);
    return precedence !== 'equal';
  }

  private getRolePriority(role: string): number {
    switch (role) {
      case 'partner': return 4;
      case 'associate': return 3;
      case 'paralegal': return 2;
      case 'clerk': return 1;
      default: return 0;
    }
  }

  /**
   * Determine if operation affects critical legal content
   */
  isCriticalLegalContent(operation: Operation, documentText: string): boolean {
    const position = operation.position;
    const contextText = this.getTextContext(documentText, position, 100);
    
    // Check for critical legal elements
    const criticalPatterns = [
      /\bWHEREAS\b/i,
      /\bNOW THEREFORE\b/i,
      /\bSIGNATURE\b/i,
      /\bBy:\s*_+/i,
      /\$[\d,]+/,  // Dollar amounts
      /\b\d{1,2}\/\d{1,2}\/\d{4}\b/, // Dates
      /\b[A-Z][a-z]+\s+[A-Z][a-z]+,\s*Esq\./  // Attorney names
    ];
    
    return criticalPatterns.some(pattern => pattern.test(contextText));
  }

  private getTextContext(text: string, position: number, radius: number): string {
    const start = Math.max(0, position - radius);
    const end = Math.min(text.length, position + radius);
    return text.slice(start, end);
  }
}

// ============================================================================
// OPERATION COMPARISON
// ============================================================================

export class OperationComparator {
  /**
   * Check if two operations are equivalent
   */
  static areEquivalent(op1: Operation, op2: Operation): boolean {
    if (op1.type !== op2.type) return false;
    if (op1.position !== op2.position) return false;
    
    if (op1.type === 'insert' && op2.type === 'insert') {
      return op1.content === op2.content;
    }
    
    if (op1.type === 'delete' && op2.type === 'delete') {
      return op1.start === op2.start && op1.end === op2.end;
    }
    
    return false;
  }

  /**
   * Check if operations conflict
   */
  static doOperationsConflict(op1: Operation, op2: Operation): boolean {
    // Same position operations always conflict
    if (op1.position === op2.position && op1.type !== op2.type) {
      return true;
    }
    
    // Overlapping delete operations conflict
    if (op1.type === 'delete' && op2.type === 'delete') {
      const range1 = { start: op1.start, end: op1.end };
      const range2 = { start: op2.start, end: op2.end };
      
      return this.rangesOverlap(range1, range2);
    }
    
    return false;
  }

  private static rangesOverlap(
    range1: { start: number; end: number },
    range2: { start: number; end: number }
  ): boolean {
    return !(range1.end <= range2.start || range2.end <= range1.start);
  }
}

// ============================================================================
// CONFLICT RESOLUTION STRATEGIES
// ============================================================================

export class ConflictResolutionStrategy {
  /**
   * Resolve conflict using legal document rules
   */
  static resolveLegalConflict(conflict: Conflict): ConflictResolution {
    const resolution: ConflictResolution = {
      conflictId: conflict.id,
      localOperation: conflict.localOperation,
      remoteOperation: conflict.remoteOperation,
      resolution: 'accept_local', // Default
      resolvedBy: conflict.localOperation.attorney,
      resolutionReason: 'Default resolution'
    };
    
    // Apply legal-specific resolution rules
    switch (conflict.type) {
      case 'position':
        resolution.resolution = 'merge';
        resolution.resolutionReason = 'Merged concurrent edits at same position';
        break;
        
      case 'content':
        // Content conflicts require more sophisticated resolution
        if (this.isFormattingChange(conflict.localOperation) && 
            this.isSubstantiveChange(conflict.remoteOperation as any)) {
          resolution.resolution = 'accept_remote';
          resolution.resolutionReason = 'Substantive changes take precedence over formatting';
        }
        break;
        
      case 'legal_precedence':
        // Apply attorney hierarchy
        resolution.resolution = 'accept_remote'; // Will be determined by precedence rules
        resolution.resolutionReason = 'Applied legal hierarchy precedence rules';
        break;
    }
    
    return resolution;
  }

  private static isFormattingChange(operation: Operation): boolean {
    // Simple heuristic - will be enhanced
    return operation.type === 'format';
  }

  private static isSubstantiveChange(operation: Operation): boolean {
    // Simple heuristic - will be enhanced
    return operation.type === 'insert' || operation.type === 'delete';
  }
}

// ============================================================================
// OPERATION VALIDATION
// ============================================================================

export class OperationValidator {
  /**
   * Validate operation before transformation
   */
  static validateOperation(operation: Operation, documentText: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    // Validate position bounds
    if (operation.position < 0) {
      errors.push(`Invalid position: ${operation.position} < 0`);
    }
    
    if (operation.position > documentText.length) {
      errors.push(`Invalid position: ${operation.position} > ${documentText.length}`);
    }
    
    // Validate operation-specific constraints
    if (operation.type === 'insert') {
      const insertOp = operation as InsertOperation;
      if (!insertOp.content || insertOp.content.length === 0) {
        errors.push('Insert operation must have content');
      }
    }
    
    if (operation.type === 'delete') {
      const deleteOp = operation as DeleteOperation;
      if (deleteOp.start >= deleteOp.end) {
        errors.push(`Invalid delete range: ${deleteOp.start} >= ${deleteOp.end}`);
      }
      
      if (deleteOp.end > documentText.length) {
        errors.push(`Delete end position exceeds document length: ${deleteOp.end} > ${documentText.length}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate transformed operation
   */
  static validateTransformedOperation(
    original: Operation,
    transformed: Operation,
    documentText: string
  ): boolean {
    // Basic validation
    const validation = this.validateOperation(transformed, documentText);
    if (!validation.isValid) {
      console.error('[OperationValidator] Transformed operation invalid:', validation.errors);
      return false;
    }
    
    // Ensure transformation preserves operation intent
    if (original.type !== transformed.type) {
      console.error('[OperationValidator] Transformation changed operation type');
      return false;
    }
    
    return true;
  }
}
